// ---------------------------------------------------------------------------
// Chart rendering — all Plotly chart functions
// ---------------------------------------------------------------------------

import type { HartEvent, TopicCluster, DailyVolume } from './types';
import { SENTIMENT_STYLES, ML_CAMPAIGN_TYPES } from './constants';
import { analyzeSentimentFromDescription, classifyHartEventByCampaignType, determineRiskLevel } from './utils';

const FONT = { family: 'Inter, sans-serif' };
const PRIMARY = '#667eea';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

/** Format YYYY-MM-DD to "Mar 16, 2026" */
function _formatDateLabel(d: string): string {
  if (!d || d === 'Recent' || !d.includes('-')) return d;
  const parts = d.split('-');
  if (parts.length < 3) return d;
  const [y, m, day] = parts;
  const month = MONTHS[parseInt(m, 10) - 1] || m;
  return `${month} ${parseInt(day, 10)}, ${y}`;
}

const MAX_X_TICKS = 4;
const LAST_N_FULL = 3;

/** X-axis opts: last 3 dates shown fully; earlier dates collapsed to 4 ticks */
function _dateAxisOpts(dates: string[]): { tickvals: string[]; ticktext: string[] } {
  if (dates.length <= MAX_X_TICKS + LAST_N_FULL) {
    return { tickvals: dates, ticktext: dates.map(_formatDateLabel) };
  }
  const last3 = dates.slice(-LAST_N_FULL);
  const rest = dates.slice(0, -LAST_N_FULL);
  let earlier: string[];
  if (rest.length <= MAX_X_TICKS) {
    earlier = rest;
  } else {
    const n = rest.length;
    const indices = [0, Math.floor(n / 3), Math.floor((2 * n) / 3), n - 1];
    earlier = [...new Set(indices)].sort((a, b) => a - b).map(i => rest[i]);
  }
  const tickvals = [...earlier, ...last3];
  return { tickvals, ticktext: tickvals.map(_formatDateLabel) };
}

// Disable Plotly's loading spinner and modebar (modebar loading dots can persist)
const PLOTLY_CONFIG = {
  responsive: true,
  staticPlot: true,
  displayModeBar: false,
  displaylogo: false,
};

/** Purge and plot — clears stuck loading state before rendering */
function _plot(container: HTMLElement, data: unknown[], layout: Record<string, unknown>): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const P = (window as any).Plotly;
  if (P?.purge) P.purge(container);
  container.innerHTML = '';
  const plotDiv = document.createElement('div');
  plotDiv.style.width = '100%';
  plotDiv.style.height = '100%';
  container.appendChild(plotDiv);
  P.newPlot(plotDiv, data, layout, PLOTLY_CONFIG).then(() => {
    const remove = (root: ParentNode) => root.querySelectorAll('.modebar, .modebar-container, [class*="modebar"], .plotly-notifier, [class*="loading"]').forEach((el) => el.remove());
    remove(plotDiv);
    remove(container);
    document.querySelectorAll('.plotly-notifier').forEach((el) => el.remove());
  });
}

// ── HART-specific charts ────────────────────────────────────────────────────

export function createHartCharts(events: HartEvent[]): void {
  if (!events || events.length === 0) {
    // Replace loading spinners with static "no data" placeholders
    const sentEl = document.getElementById('sentimentChart');
    const spikeEl = document.getElementById('spikesChart');
    const campEl = document.getElementById('campaignChart');
    if (sentEl) _placeholder(sentEl, 'fa-chart-pie', 'No sentiment data available');
    if (spikeEl) _placeholder(spikeEl, 'fa-chart-line', 'No activity data available');
    if (campEl) _placeholder(campEl, 'fa-tags', 'No campaign type data');
    return;
  }
  _hartSentimentPie(events);
  _hartCampaignBar(events);
  _hartSpikeLine(events);
}

function _hartSentimentPie(events: HartEvent[]): void {
  const container = document.getElementById('sentimentChart');
  if (!container) return;
  const counts: Record<string, number> = {};
  events.forEach(e => { const s = analyzeSentimentFromDescription(e.descBody); counts[s] = (counts[s] || 0) + 1; });
  if (Object.keys(counts).length === 0) { _placeholder(container, 'fa-chart-pie', 'No sentiment data available'); return; }

  const labels: string[] = [], values: number[] = [], colors: string[] = [];
  for (const [s, c] of Object.entries(counts)) {
    const cfg = SENTIMENT_STYLES[s] || { label: s, color: '#4299e1' };
    labels.push(cfg.label); values.push(c); colors.push(cfg.color);
  }
  _plot(container,
    [{ labels, values, type: 'pie', marker: { colors }, textinfo: 'label+percent', hovertemplate: '<b>%{label}</b><br>Events: %{value}<br>%{percent}<extra></extra>' }],
    { title: 'Sentiment Distribution', margin: { t: 50, b: 50, l: 50, r: 50 }, font: FONT, showlegend: true });
}

function _hartCampaignBar(events: HartEvent[]): void {
  const container = document.getElementById('campaignChart');
  if (!container) return;
  const counts: Record<string, number> = {};
  events.forEach(e => { const t = classifyHartEventByCampaignType(e); counts[t] = (counts[t] || 0) + 1; });
  if (Object.keys(counts).length === 0) { _placeholder(container, 'fa-tags', 'No campaign type data'); return; }

  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
  _plot(container,
    [{ x: sorted.map(([t]) => t), y: sorted.map(([, c]) => c), type: 'bar', marker: { color: PRIMARY }, hovertemplate: '<b>%{x}</b><br>Events: %{y}<extra></extra>' }],
    { title: 'Campaign Type Distribution', xaxis: { title: 'Campaign Type', tickangle: -45 }, yaxis: { title: 'Events' }, margin: { t: 50, b: 120, l: 60, r: 50 }, font: FONT });
}

function _hartSpikeLine(events: HartEvent[]): void {
  const container = document.getElementById('spikesChart');
  if (!container) return;
  const byDate: Record<string, number> = {};
  events.forEach(e => {
    const m = (e.descBody || '').match(/(?:On|From)\s+(\d{1,2}\s+\w+\s+\d{4})/i);
    let key = 'Recent';
    if (m) { try { const d = new Date(m[1]); if (!isNaN(d.getTime())) key = d.toISOString().split('T')[0]; } catch { /* use Recent */ } }
    byDate[key] = (byDate[key] || 0) + 1;
  });
  if (Object.keys(byDate).length === 0) { _placeholder(container, 'fa-chart-line', 'No activity data'); return; }

  const dates = Object.keys(byDate).sort();
  const ri = dates.indexOf('Recent');
  if (ri !== -1) { const v = byDate['Recent']; dates.splice(ri, 1); dates.push('Recent'); byDate['Recent'] = v; }
  const values = dates.map(d => byDate[d]);

  const axisOpts = _dateAxisOpts(dates);
  _plot(container,
    [{ x: dates, y: values, type: 'scatter', mode: 'lines+markers', name: 'Event Volume', line: { color: PRIMARY, width: 3 }, marker: { size: 10, color: PRIMARY }, fill: 'tozeroy', fillcolor: 'rgba(102,126,234,0.1)', hovertemplate: '<b>%{x}</b><br>Events: %{y}<extra></extra>' }],
    { title: 'Event Activity Over Time', xaxis: { title: 'Date', showgrid: true, tickvals: axisOpts.tickvals, ticktext: axisOpts.ticktext, tickangle: -45, automargin: true }, yaxis: { title: 'Events', showgrid: true }, margin: { t: 50, b: 100, l: 60, r: 50 }, font: FONT, plot_bgcolor: '#fafafa', paper_bgcolor: 'white' });
}

// ── HART analytics charts ───────────────────────────────────────────────────

function _eventDate(ev: HartEvent): string {
  const ts = ev.createdAt || ev.reportedAt;
  if (!ts) return 'Recent';
  try {
    const d = new Date(ts);
    return isNaN(d.getTime()) ? 'Recent' : d.toISOString().split('T')[0];
  } catch { return 'Recent'; }
}

export function createHartAnalyticsCharts(events: HartEvent[]): void {
  // Event volume timeline (by date)
  const timelineCont = document.getElementById('eventVolumeChart');
  if (timelineCont && events.length > 0) {
    const byDate: Record<string, number> = {};
    events.forEach(e => { const d = _eventDate(e); byDate[d] = (byDate[d] || 0) + 1; });
    const dates = Object.keys(byDate).filter(d => d !== 'Recent').sort();
    if (dates.length > 0) {
      const values = dates.map(d => byDate[d]);
      const axisOpts = _dateAxisOpts(dates);
      _plot(timelineCont,
        [{ x: dates, y: values, type: 'scatter', mode: 'lines+markers', name: 'Event Volume', line: { color: PRIMARY, width: 3 }, marker: { size: 10, color: PRIMARY }, fill: 'tozeroy', fillcolor: 'rgba(102,126,234,0.1)', hovertemplate: '<b>%{x}</b><br>Events: %{y}<extra></extra>' }],
        { title: 'Event Volume Over Time', xaxis: { title: 'Date', showgrid: true, tickvals: axisOpts.tickvals, ticktext: axisOpts.ticktext, tickangle: -45, automargin: true }, yaxis: { title: 'Events', showgrid: true }, margin: { t: 50, b: 100, l: 60, r: 50 }, font: FONT, plot_bgcolor: '#fafafa', paper_bgcolor: 'white' });
    } else {
      _placeholder(timelineCont, 'fa-chart-line', 'No date data in events');
    }
  }

  // Campaign distribution (main)
  const evoCont = document.getElementById('evolutionChart');
  if (evoCont) {
    const counts: Record<string, number> = {};
    events.forEach(e => { const t = (e.mlCampaignType as string) || classifyHartEventByCampaignType(e); counts[t] = (counts[t] || 0) + 1; });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const barColors = ['#667eea','#764ba2','#11998e','#38ef7d','#f56565','#ed8936','#48bb78','#4299e1','#9f7aea','#f093fb','#f5af19'];
    _plot(evoCont,
      [{ x: sorted.map(([t]) => t), y: sorted.map(([, c]) => c), type: 'bar', marker: { color: sorted.map((_, i) => barColors[i % barColors.length]) }, text: sorted.map(([, c]) => `${c} events`), textposition: 'auto', hovertemplate: '<b>%{x}</b><br>Events: %{y}<extra></extra>' }],
      { title: `Campaign Type Distribution (${events.length} Events)`, xaxis: { title: 'Campaign Type', tickangle: -45, automargin: true }, yaxis: { title: 'Events' }, margin: { t: 50, b: 120, l: 50, r: 50 }, font: FONT });
  }

  // Sentiment pie
  const sentCont = document.getElementById('sentimentEvolution');
  if (sentCont) {
    const counts: Record<string, number> = { positive: 0, negative: 0, neutral: 0, mixed: 0 };
    events.forEach(e => { counts[analyzeSentimentFromDescription(e.descBody)]++; });
    const labels: string[] = [], values: number[] = [], colors: string[] = [];
    for (const [s, c] of Object.entries(counts)) {
      if (c > 0) { const cfg = SENTIMENT_STYLES[s]; labels.push(cfg.label); values.push(c); colors.push(cfg.color); }
    }
    _plot(sentCont,
      [{ labels, values, type: 'pie', marker: { colors }, textinfo: 'label+percent', hovertemplate: '<b>%{label}</b><br>Events: %{value}<br>%{percent}<extra></extra>' }],
      { title: 'Sentiment Distribution', margin: { t: 50, b: 50, l: 50, r: 50 }, font: FONT, showlegend: true });
  }

  // Risk pie
  const riskCont = document.getElementById('campaignEvolution');
  if (riskCont) {
    const r = { HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 };
    events.forEach(e => { r[determineRiskLevel(e.descBody || '').level]++; });
    _plot(riskCont,
      [{ labels: ['High Risk','Medium Risk','Low Risk','Info'], values: Object.values(r), type: 'pie', marker: { colors: ['#f56565','#ed8936','#48bb78','#4299e1'] }, textinfo: 'label+percent+value', hovertemplate: '<b>%{label}</b><br>Events: %{value}<br>%{percent}<extra></extra>' }],
      { title: 'Risk Level Distribution', margin: { t: 50, b: 50, l: 50, r: 50 }, font: FONT, showlegend: true });
  }
}

// ── LifeRaft topic-based charts ─────────────────────────────────────────────

export function createSpikesChartFromDailyVolumes(volumes: DailyVolume[]): void {
  const container = document.getElementById('spikesChart');
  if (!container) return;
  if (!volumes?.length || volumes.every(v => v.count === 0)) {
    _placeholder(container, 'fa-chart-line', 'No activity data available');
    return;
  }

  const dates = volumes.map(v => v.date);
  const values = volumes.map(v => v.count);
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  const spikeThreshold = Math.max(avg * 1.5, avg + 1);
  const markerColors = values.map(v => (v >= spikeThreshold ? '#f56565' : PRIMARY));
  const markerSizes = values.map(v => (v >= spikeThreshold ? 14 : 10));
  const axisOpts = _dateAxisOpts(dates);

  _plot(container,
    [{
      x: dates,
      y: values,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Post Volume',
      line: { color: PRIMARY, width: 3 },
      marker: { size: markerSizes, color: markerColors },
      fill: 'tozeroy',
      fillcolor: 'rgba(102,126,234,0.1)',
      hovertemplate: '<b>%{x}</b><br>Posts: %{y}<extra></extra>',
    }],
    {
      title: 'Trend Spike Analysis',
      xaxis: { title: 'Date', tickvals: axisOpts.tickvals, ticktext: axisOpts.ticktext, showgrid: true, tickangle: -45, automargin: true },
      yaxis: { title: 'Posts', showgrid: true },
      margin: { t: 50, b: 100, l: 60, r: 50 },
      font: FONT,
      plot_bgcolor: '#fafafa',
      paper_bgcolor: 'white',
      shapes: avg > 0 ? [{
        type: 'line',
        xref: 'paper',
        x0: 0,
        x1: 1,
        y0: avg,
        y1: avg,
        line: { color: '#a0aec0', width: 1, dash: 'dash' },
      }] : [],
      annotations: avg > 0 ? [{
        xref: 'paper',
        x: 1,
        y: avg,
        xanchor: 'left',
        yanchor: 'bottom',
        text: `Avg ${Math.round(avg)}`,
        showarrow: false,
        font: { size: 10, color: '#718096' },
      }] : [],
    });
}

export function createSpikesChartFromTopics(topics: TopicCluster[]): void {
  const container = document.getElementById('spikesChart');
  if (!container) return;
  if (!topics || topics.length === 0) { _placeholder(container, 'fa-chart-line', 'No activity data available'); return; }

  const byDate: Record<string, number> = {};
  topics.forEach(t => {
    const d = t.analysis_date || (t.date ? new Date(t.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    byDate[d] = (byDate[d] || 0) + t.size;
  });
  const volumes: DailyVolume[] = Object.keys(byDate).sort().map(date => ({ date, count: byDate[date] }));
  createSpikesChartFromDailyVolumes(volumes);
}

export function createSentimentChartFromTopics(topics: TopicCluster[]): void {
  const container = document.getElementById('sentimentChart');
  if (!container) return;
  if (!topics || topics.length === 0) { _placeholder(container, 'fa-chart-pie', 'No sentiment data available'); return; }

  const counts: Record<string, number> = {};
  topics.forEach(t => { const s = (t.dominant_sentiment || 'neutral').toLowerCase(); counts[s] = (counts[s] || 0) + t.size; });
  if (Object.keys(counts).length === 0) { _placeholder(container, 'fa-chart-pie', 'No sentiment data available'); return; }

  const labels: string[] = [], values: number[] = [], colors: string[] = [];
  for (const [s, c] of Object.entries(counts)) {
    const cfg = SENTIMENT_STYLES[s] || { label: s, color: '#4299e1' };
    labels.push(cfg.label); values.push(c); colors.push(cfg.color);
  }
  _plot(container,
    [{ labels, values, type: 'pie', marker: { colors }, textinfo: 'label+percent', hovertemplate: '<b>%{label}</b><br>Posts: %{value}<br>%{percent}<extra></extra>' }],
    { title: 'Sentiment Distribution (by Posts)', margin: { t: 50, b: 50, l: 50, r: 50 }, font: FONT, showlegend: true });
}

export function createCampaignTypeChartFromTopics(topics: TopicCluster[]): void {
  const container = document.getElementById('campaignChart');
  if (!container) return;
  if (!topics || topics.length === 0) { _placeholder(container, 'fa-tags', 'No campaign type data available'); return; }

  const counts: Record<string, number> = {};
  topics.forEach(t => { const c = t.dominant_campaign_type || 'Unknown'; counts[c] = (counts[c] || 0) + t.size; });

  _plot(container,
    [{ x: Object.keys(counts), y: Object.values(counts), type: 'bar', marker: { color: PRIMARY } }],
    { title: 'Campaign Type Distribution', xaxis: { title: 'Campaign Type' }, yaxis: { title: 'Posts' }, margin: { t: 50, b: 100, l: 50, r: 50 }, font: FONT });
}

export function createTopicAnalyticsCharts(topics: TopicCluster[]): void {
  if (!topics || topics.length === 0) return;

  // Activity timeline (posts per date)
  const timelineCont = document.getElementById('eventVolumeChart');
  if (timelineCont) {
    const byDate: Record<string, number> = {};
    topics.forEach(t => {
      const d = t.date ? new Date(t.date).toISOString().split('T')[0] : (t.analysis_date || new Date().toISOString().split('T')[0]);
      byDate[d] = (byDate[d] || 0) + (t.size || 0);
    });
    const dates = Object.keys(byDate).sort();
    if (dates.length > 0) {
      const axisOpts = _dateAxisOpts(dates);
      _plot(timelineCont,
        [{ x: dates, y: dates.map(d => byDate[d]), type: 'scatter', mode: 'lines+markers', name: 'Post Volume', line: { color: PRIMARY, width: 3 }, marker: { size: 10, color: PRIMARY }, fill: 'tozeroy', fillcolor: 'rgba(102,126,234,0.1)', hovertemplate: '<b>%{x}</b><br>Posts: %{y}<extra></extra>' }],
        { title: 'Post Volume Over Time', xaxis: { title: 'Date', showgrid: true, tickvals: axisOpts.tickvals, ticktext: axisOpts.ticktext, tickangle: -45, automargin: true }, yaxis: { title: 'Posts', showgrid: true }, margin: { t: 50, b: 100, l: 60, r: 50 }, font: FONT, plot_bgcolor: '#fafafa', paper_bgcolor: 'white' });
    } else {
      _placeholder(timelineCont, 'fa-chart-line', 'No date data in topics');
    }
  }

  // Cluster sizes bar chart
  const evoCont = document.getElementById('evolutionChart');
  if (evoCont) {
    const labels = topics.map(t => t.dominant_campaign_type || (t.keywords?.slice(0, 2).join(', ') || 'Unknown'));
    _plot(evoCont,
      [{ x: labels, y: topics.map(t => t.size), type: 'bar', marker: { color: topics.map(t => t.coherence_score || 0.5), colorscale: 'Viridis', showscale: true, colorbar: { title: 'Coherence' } }, text: topics.map((t, i) => `${labels[i]}<br>${t.size} posts`), textposition: 'auto', hovertemplate: '<b>%{text}</b><br>Posts: %{y}<extra></extra>' }],
      { title: 'Topic Cluster Sizes by Campaign Type', xaxis: { title: 'Campaign Types', tickangle: -45, automargin: true }, yaxis: { title: 'Posts' }, margin: { t: 50, b: 120, l: 50, r: 50 }, font: FONT });
  }

  // Sentiment distribution pie
  const sentCont = document.getElementById('sentimentEvolution');
  if (sentCont) {
    const counts = topics.reduce<Record<string, number>>((a, t) => { const s = t.dominant_sentiment || 'unknown'; a[s] = (a[s] || 0) + t.size; return a; }, {});
    _plot(sentCont,
      [{ labels: Object.keys(counts), values: Object.values(counts), type: 'pie', marker: { colors: ['#48bb78','#ed8936','#f56565','#4299e1','#9f7aea'] } }],
      { title: 'Overall Sentiment Distribution', margin: { t: 50, b: 50, l: 50, r: 50 }, font: FONT });
  }

  // Campaign type bar
  const campCont = document.getElementById('campaignEvolution');
  if (campCont) {
    const counts: Record<string, number> = {};
    topics.forEach(t => { if (t.dominant_campaign_type) counts[t.dominant_campaign_type] = (counts[t.dominant_campaign_type] || 0) + t.size; });
    if (Object.keys(counts).length > 0) {
      _plot(campCont,
        [{ x: Object.keys(counts), y: Object.values(counts), type: 'bar', marker: { color: PRIMARY } }],
        { title: 'Campaign Type Distribution', xaxis: { title: 'Campaign Type' }, yaxis: { title: 'Posts' }, margin: { t: 50, b: 100, l: 50, r: 50 }, font: FONT });
    }
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function _placeholder(el: HTMLElement, icon: string, msg: string): void {
  el.innerHTML = `<div class="chart-placeholder"><i class="fas ${icon}"></i><p>${msg}</p></div>`;
}

